import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, BookOpen, ChevronDown, ChevronUp, Copy, FileText, GitCompare, History, ListChecks,
  Megaphone, MessageSquare, Mic, Phone, PhoneCall, Play, Plus, RefreshCw, Rocket, Save, Send, Settings2,
  ShieldCheck, Sparkles, Square, Trash2, Wrench,
} from "lucide-react";
import { api, API, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Select, Waveform, Toggle, StatusPill, Modal } from "@/components/bits";
import VoiceTestPanel from "@/components/VoiceTestPanel";

// ---------- defaults (mirrors backend data.py when /options is older) ----------
const FALLBACK_GUARDRAILS = {
  daily_messages_per_lead: 3, max_messages_per_lead: 8, max_call_attempts: 3, daily_calls_total: 200,
  call_window_start: "09:00", call_window_end: "19:00", timezone: "Asia/Kolkata",
  min_minutes_between_calls: 120, min_minutes_between_messages: 30, stop_on_reply: true,
};
const FALLBACK_SEQUENCE = [
  { step: 1, channel: "whatsapp", delay_minutes: 0, label: "WhatsApp template nudge" },
  { step: 2, channel: "voice_call", delay_minutes: 45, label: "Voice call (local dialect)" },
  { step: 3, channel: "voice_call", delay_minutes: 1440, label: "Follow-up call next day" },
];
const FALLBACK_CHANNELS = [
  { id: "whatsapp", name: "WhatsApp", desc: "Template first; free-form after the lead replies" },
  { id: "voice_call", name: "Voice call", desc: "ElevenLabs outbound call in the agent language" },
];
const FALLBACK_VOICES = [
  { id: "aarav", name: "Aarav R", gender: "Male", tone: "Natural, deep Indian Hindi" },
  { id: "pK6d9s3qOoKanWljVcOX", name: "Priya", gender: "Female", tone: "Polished and composed · Bihari" },
  { id: "gHu9GtaHOXcSqFTK06ux", name: "Anjali", gender: "Female", tone: "Warm, cheerful and clear" },
  { id: "XxQtFmwKGEygx1x7UMJ5", name: "Jeet", gender: "Male", tone: "Regional customer care · Bihari" },
];
const TIMEZONES = ["Asia/Kolkata", "Asia/Kathmandu", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Europe/London", "UTC"];
const LOCKED_FIELDS = [
  "id", "_id", "company_id", "version", "created_at", "updated_at", "calls", "published_at", "duplicated_from",
  "elevenlabs_agent_id", "elevenlabs_agent_ids", "elevenlabs_agents", "elevenlabs_voice_id", "elevenlabs_voice_key", "elevenlabs_language",
  "elevenlabs_unsupported_languages", "elevenlabs_kb", "voice_published_at", "voice_prompt_version",
];
const TABS = [
  { id: "basics", label: "Basics", icon: Settings2 },
  { id: "prompts", label: "Prompts", icon: FileText },
  { id: "channels", label: "Channels & templates", icon: MessageSquare },
  { id: "guardrails", label: "Guardrails", icon: ShieldCheck },
  { id: "sequence", label: "Outreach sequence", icon: ListChecks },
  { id: "knowledge", label: "Knowledge & tools", icon: Wrench },
  { id: "test", label: "Test", icon: PhoneCall },
  { id: "campaigns", label: "Campaigns", icon: Megaphone, aux: true },
  { id: "runs", label: "Runs", icon: Activity, aux: true },
  { id: "history", label: "History", icon: History, aux: true },
];

const numOrEmpty = (v) => (v === "" ? "" : Number(v));
const fmtDelay = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  if (m < 60) return `${m}m`;
  if (m < 1440) { const h = m / 60; return `${Number.isInteger(h) ? h : h.toFixed(1)}h`; }
  const d = Math.floor(m / 1440); const h = Math.round((m % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
};
const agentBody = (a) => { const body = { ...a }; LOCKED_FIELDS.forEach((k) => delete body[k]); delete body.sms_template; return body; };
const stripSmsSteps = (steps = []) => (steps || [])
  .filter((s) => (s?.channel || "") !== "sms")
  .map((s, i) => ({ ...s, step: i + 1 }));
const normalizeAgent = (a) => {
  const clean = { ...a };
  delete clean.sms_template;
  const channels = (a.channels || ["whatsapp", "voice_call"]).filter((c) => c !== "sms");
  return {
    ...clean,
    audience: a.audience || "farmer",
    channels: channels.length ? channels : ["whatsapp", "voice_call"],
    guardrails: a.guardrails || {},
    channel_prompts: Object.fromEntries(Object.entries(a.channel_prompts || {}).filter(([k]) => k !== "sms")),
    additional_languages: a.additional_languages || [],
    kb_sources: a.kb_sources || [],
    kb: a.kb || [],
    outreach_sequence: stripSmsSteps(a.outreach_sequence || []),
    custom_tools: a.custom_tools || [],
    whatsapp_template: a.whatsapp_template || {},
    workflow_ids: a.workflow_ids || [],
    active: a.active !== false,
  };
};
const defaultFirstMessage = (audience) => (audience === "retailer"
  ? "Namaste {name} ji! Main {agent} bol raha hoon, {company} se. Aapki shop ke stock aur is hafte ki demand par 2 minute baat kar sakte hain?"
  : "Namaste {name}! Main {agent} bol raha hoon, {company} se. Aapki fasal ke baare mein 2 minute ki zaroori baat karni hai — bol sakte hain?");
const metaSummary = (m) => {
  if (!m || typeof m !== "object") return "";
  return Object.entries(m).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v && typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");
};

// ---------- version publish diff ----------
const DIFF_FIELD_LABELS = {
  name: "Name", audience: "Audience", state: "State", language: "Language", dialect: "Dialect", model: "Model",
  voice: "Voice", prompt: "System prompt", first_message: "Voice opener", goal: "Success goal", crops: "Season crops",
  channels: "Channels", channel_prompts: "Channel prompts", guardrails: "Guardrails", outreach_sequence: "Outreach sequence",
  kb_sources: "Knowledge sources", kb: "Custom knowledge", tools: "Tools", workflow_ids: "Workflow tools",
  custom_tools: "Custom tools", whatsapp_template: "WhatsApp template",
  phone_number_id: "Caller ID", additional_languages: "Additional languages", active: "Active",
};
const diffPreview = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "string") return v.length > 240 ? `${v.slice(0, 240)}…` : v;
  if (typeof v === "boolean") return v ? "on" : "off";
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length === 1 ? "" : "s"} · ${diffPreview(v.map((x) => (x && typeof x === "object" ? x.name || x.channel || "…" : x)).join(", "))}` : "empty";
  if (typeof v === "object") return diffPreview(JSON.stringify(v));
  return String(v);
};

const DiffList = ({ changes, empty = "No differences — the current draft matches." }) => {
  const visible = (changes || []).filter((c) => c.field !== "sms_template");
  return (
  <div className="space-y-2" data-testid="diff-list">
    {!visible.length && <p className="text-sm text-slate-500">{empty}</p>}
    {visible.map((c) => (
      <div key={c.field} className="glass-soft p-3" data-testid={`diff-row-${c.field}`}>
        <p className="text-[11px] font-mono uppercase tracking-wide text-slate-400 mb-1.5">{DIFF_FIELD_LABELS[c.field] || c.field.replace(/_/g, " ")}</p>
        <div className="grid sm:grid-cols-2 gap-2 text-xs">
          <div className="min-w-0"><p className="text-[10px] font-mono uppercase text-rose-300 mb-0.5">before</p><p className="text-slate-400 break-words">{diffPreview(c.before)}</p></div>
          <div className="min-w-0"><p className="text-[10px] font-mono uppercase text-emerald-300 mb-0.5">after</p><p className="text-slate-200 break-words">{diffPreview(c.after)}</p></div>
        </div>
      </div>
    ))}
  </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="field-label">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </div>
);
const Card = ({ title, desc, action, children, testId }) => (
  <div className="glass p-6 rise space-y-4" data-testid={testId}>
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
      <div>
        <h3 className="font-display font-semibold text-slate-100">{title}</h3>
        {desc && <p className="text-xs text-slate-500 mt-1 max-w-2xl">{desc}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export default function AgentBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { company } = useAuth();
  const [opts, setOpts] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [integration, setIntegration] = useState(null);
  const [audience, setAudience] = useState(company?.channels?.includes("hyperlocal") ? "farmer" : "retailer");
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState("basics");
  const [testTab, setTestTab] = useState("chat");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [voicePublishing, setVoicePublishing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [publishPreview, setPublishPreview] = useState(null); // {changes, first_publish, base_version} | null
  const [diffBusy, setDiffBusy] = useState(false);
  const previewAudioRef = useRef(null);

  useEffect(() => {
    api.get("/options").then((r) => setOpts(r.data)).catch(() => setOpts({}));
    api.get("/templates").then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
    api.get("/workflows").then((r) => setWorkflows((r.data || []).filter((w) => w.is_tool))).catch(() => {});
    api.get("/integrations/elevenlabs").then((r) => setIntegration(r.data)).catch(() => setIntegration({ configured: false, voices: [], phone_numbers: [] }));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoadFailed(false);
    api.get(`/agents/${id}`).then((r) => { setAgent(normalizeAgent(r.data)); setAudience(r.data.audience || "farmer"); setDirty(false); })
      .catch(() => { setLoadFailed(true); toast.error("Could not load this agent"); });
  }, [id]);

  useEffect(() => {
    if (!agent || !workflows.length || (agent.workflow_ids || []).length) return;
    const slugFor = { po: "order_booking", so: "create_sales_order", inventory: "inventory_check", nearest_retailer: "nearest_retailer", weather: "weather_satellite" };
    const ids = workflows.filter((w) => (agent.tools || []).some((tool) => slugFor[tool] === w.slug)).map((w) => w.id);
    if (ids.length) setAgent((current) => ((current.workflow_ids || []).length ? current : { ...current, workflow_ids: ids }));
  }, [workflows, agent]);

  const markDirty = () => setDirty(true);
  const set = (k, v) => { setAgent((a) => ({ ...a, [k]: v })); markDirty(); };
  const setValue = (k, fn) => { setAgent((a) => ({ ...a, [k]: fn(a[k]) })); markDirty(); };
  const toggle = (k, v) => setValue(k, (list) => { const arr = list || []; return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]; });

  const gr = useMemo(() => ({ ...(opts?.guardrail_defaults || FALLBACK_GUARDRAILS), ...(agent?.guardrails || {}) }), [opts, agent?.guardrails]);
  const setGuard = (k, v) => set("guardrails", { ...(agent.guardrails || {}), [k]: v });
  const channels = (opts?.channels || FALLBACK_CHANNELS).filter((c) => c.id !== "sms");
  const channelOptions = (opts?.outreach_channels || channels).filter((c) => c.id !== "sms").map((c) => ({ value: c.id, label: c.name }));
  const channelName = (cid) => channels.find((c) => c.id === cid)?.name || cid;
  const channelIcon = (cid, cls = "w-3.5 h-3.5") => (cid === "voice_call" ? <Phone className={cls} /> : <MessageSquare className={cls} />);
  const defaultSequence = stripSmsSteps(opts?.default_outreach_sequence || FALLBACK_SEQUENCE);
  const seq = agent?.outreach_sequence || [];
  const kb = agent?.kb || [];
  const ct = agent?.custom_tools || [];
  const wa = agent?.whatsapp_template || {};
  const phoneOptions = ((integration?.phone_numbers) || []).map((p) => ({ value: p.phone_number_id || p.id, label: p.phone_number || p.name || p.phone_number_id || p.id }));
  const voices = useMemo(() => {
    const base = opts?.voices?.length ? opts.voices : FALLBACK_VOICES;
    const selected = agent?.voice;
    if (selected && !base.some((v) => v.id === selected)) {
      return [{ id: selected, name: `${selected} (unavailable)`, gender: "—", tone: "Pick one of the four supported voices", unavailable: true }, ...base];
    }
    return base;
  }, [opts?.voices, agent?.voice]);
  const liveVoiceFor = (v) => {
    const live = integration?.voices || [];
    return live.find((row) => row.voice_id === v.id)
      || (v.id === "aarav" ? live.find((row) => /aarav/i.test(row.name || "")) : null);
  };
  const modelOptions = useMemo(() => {
    const rows = opts?.models || [];
    const has = rows.some((m) => m.id === agent?.model);
    const merged = agent?.model && !has ? [...rows, { id: agent.model, name: agent.model, tag: "saved" }] : rows;
    return merged.map((m) => ({ value: m.id, label: `${m.name}${m.tag ? ` · ${m.tag}` : ""}${m.deprecated ? " (deprecated)" : ""}` }));
  }, [opts, agent?.model]);

  const toggleVoicePreview = (voiceId) => {
    if (playingVoice === voiceId && previewAudioRef.current) {
      previewAudioRef.current.pause();
      setPlayingVoice(null);
      return;
    }
    if (previewAudioRef.current) previewAudioRef.current.pause();
    const el = new Audio(`${API}/voices/${voiceId}/preview`);
    previewAudioRef.current = el;
    setPlayingVoice(voiceId);
    el.onended = () => setPlayingVoice(null);
    el.onerror = () => { setPlayingVoice(null); toast.error("No preview is available for this voice yet"); };
    el.play().catch(() => { setPlayingVoice(null); toast.error("Could not play the voice preview"); });
  };
  useEffect(() => () => previewAudioRef.current?.pause(), []);

  // ---------- actions ----------
  const pickTemplate = (t) => {
    const voiceOptions = opts?.voices?.length ? opts.voices : FALLBACK_VOICES;
    const templateVoice = voiceOptions.some((v) => v.id === t.voice) ? t.voice : "aarav";
    setAgent(normalizeAgent({
      name: t.name, audience: t.audience, template_id: t.id, state: t.state, language: t.language, dialect: t.dialect,
      voice: templateVoice, model: t.model, prompt: t.prompt, kb_sources: t.kb_sources || [], tools: t.tools || [], crops: t.crops,
      status: "draft", active: true,
      first_message: defaultFirstMessage(t.audience),
      channels: ["whatsapp", "voice_call"],
      guardrails: { ...(opts?.guardrail_defaults || FALLBACK_GUARDRAILS) },
      outreach_sequence: defaultSequence.map((s) => ({ ...s })),
    }));
    setDirty(true);
  };

  const save = async () => {
    if (!agent) return null;
    setSaving(true);
    try {
      const body = agentBody(agent);
      const { data } = id ? await api.put(`/agents/${id}`, body) : await api.post("/agents", body);
      toast.success(id ? "Draft saved" : "Agent created");
      setDirty(false);
      if (!id) nav(`/dashboard/agents/${data.id}`, { replace: true });
      else setAgent(normalizeAgent(data));
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return null;
    } finally { setSaving(false); }
  };

  const doPublish = async () => {
    if (!agent) return;
    setPublishing(true);
    try {
      let aid = id;
      if (!aid) {
        const created = await api.post("/agents", agentBody(agent));
        aid = created.data.id;
      } else if (dirty) {
        await api.put(`/agents/${aid}`, agentBody(agent));
      }
      const { data } = await api.post(`/agents/${aid}/publish`, {}, { timeout: 90000 });
      const kb = data.elevenlabs?.kb_docs ? ` · ${data.elevenlabs.kb_docs} KB doc${data.elevenlabs.kb_docs === 1 ? "" : "s"} synced` : "";
      toast.success(`Published v${data.version}${data.elevenlabs?.agent_id ? " · voice synced" : ""}${kb}`);
      if (data.elevenlabs?.error) toast.warning(`Voice sync skipped: ${data.elevenlabs.error}`);
      setDirty(false);
      setPublishPreview(null);
      if (!id) nav(`/dashboard/agents/${aid}`, { replace: true });
      else { const fresh = await api.get(`/agents/${aid}`); setAgent(normalizeAgent(fresh.data)); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  /** Show what publishing would change before doing it. */
  const openPublish = async () => {
    if (!agent || publishing || diffBusy) return;
    if (!id) { await doPublish(); return; } // first publish — nothing to diff against yet
    setDiffBusy(true);
    try {
      if (dirty) {
        const saved = await save();
        if (!saved) return;
      }
      const { data } = await api.get(`/agents/${id}/publish-diff`);
      setPublishPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not compute the publish diff");
    } finally { setDiffBusy(false); }
  };

  const publishVoice = async () => {
    if (!id) return toast.error("Save the agent first, then publish the voice.");
    setVoicePublishing(true);
    try {
      const { data } = await api.post(`/agents/${id}/publish-voice`, {}, { timeout: 60000 });
      setAgent((a) => ({ ...a, elevenlabs_agent_id: data.agent_id, elevenlabs_voice_id: data.voice_id }));
      toast.success("Voice agent live on ElevenLabs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Voice publish failed");
    } finally { setVoicePublishing(false); }
  };

  const duplicate = async () => {
    if (!id) return;
    setDuplicating(true);
    try {
      const { data } = await api.post(`/agents/${id}/duplicate`);
      toast.success(`Duplicated as “${data.name}” (draft)`);
      nav(`/dashboard/agents/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    } finally { setDuplicating(false); }
  };

  const loadSnapshot = (snapshot, version) => {
    if (!snapshot) return toast.error("No snapshot stored for this version");
    setAgent(normalizeAgent({ ...snapshot, id }));
    setDirty(true);
    setTab("basics");
    toast.success(`v${version} loaded into the editor — review and save to apply`);
  };

  const reloadAgent = async () => {
    if (!id) return;
    try {
      const fresh = await api.get(`/agents/${id}`);
      setAgent(normalizeAgent(fresh.data));
      setDirty(false);
    } catch (e) { /* keep whatever the editor has */ }
  };

  const loadFailedCard = (id || agent) && loadFailed && !agent && (
    <div className="glass p-10 text-center rise" data-testid="builder-load-error">
      <AlertTriangle className="w-6 h-6 text-amber-300 mx-auto mb-3" />
      <h3 className="font-display text-lg font-semibold text-slate-100">Agent not found</h3>
      <p className="text-sm text-slate-400 mt-1.5">It may have been deleted, or the link is stale.</p>
      <button onClick={() => nav("/dashboard/agents")} className="btn-ghost mt-5">Back to agents</button>
    </div>
  );

  // ---------- template picker (initial create flow) ----------
  if (!agent) {
    if (loadFailedCard) return loadFailedCard;
    if (id) return <div className="text-slate-500 text-sm">Loading agent…</div>;
    const visibleTemplates = templates.filter((t) => t.audience === audience && (!company?.states?.length || company.states.includes(t.state)));
    const list = visibleTemplates.length ? visibleTemplates : templates.filter((t) => t.audience === audience);
    return (
      <div>
        <PageHeader overline="Agent builder" title="Choose a template" desc="Templates are state-wise: language, dialect, voice and seasonal crop context are preset. Customise everything after." testId="builder-template-header" />
        <div className="flex gap-1 p-1 glass-soft rounded-full w-max mb-6" data-testid="builder-audience-toggle">
          {["farmer", "retailer"].map((a) => <button key={a} onClick={() => setAudience(a)} className={`px-4 py-2 rounded-full text-sm capitalize ${audience === a ? (a === "farmer" ? "bg-emerald-500 text-[#06110A]" : "bg-amber-500 text-[#1A1000]") + " font-semibold" : "text-slate-400"}`} data-testid={`builder-audience-${a}`}>{a}</button>)}
        </div>
        {templates.length === 0 && <p className="text-slate-500 text-sm">No templates in the catalog yet. An AgriVoice admin can add them.</p>}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((t) => (
            <button key={t.id} onClick={() => pickTemplate(t)} className="glass p-5 text-left hover:scale-[1.02] hover:border-emerald-500/40 transition-[transform,border-color] rise" data-testid={`template-${t.id}`}>
              <div className="flex items-center justify-between"><span className={t.audience === "farmer" ? "pill" : "pill-gold"}>{t.state}</span><span className="text-[11px] font-mono text-slate-500">{t.language}</span></div>
              <h3 className="font-display font-semibold text-slate-100 mt-4">{t.name}</h3>
              <p className="text-xs text-slate-400 mt-1.5">{t.summary}</p>
              <p className="text-[11px] text-slate-600 mt-3 flex items-center gap-1"><Mic className="w-3 h-3" /> {t.voice} · {t.dialect}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- builder ----------
  const seqOps = {
    update: (i, patch) => setValue("outreach_sequence", (list) => (list || []).map((s, k) => (k === i ? { ...s, ...patch } : s))),
    add: () => setValue("outreach_sequence", (list) => [...(list || []), { step: (list || []).length + 1, channel: (channelOptions[0] || { value: "whatsapp" }).value, delay_minutes: 60, label: "" }]),
    remove: (i) => setValue("outreach_sequence", (list) => (list || []).filter((_, k) => k !== i).map((s, k) => ({ ...s, step: k + 1 }))),
    move: (i, dir) => setValue("outreach_sequence", (list) => {
      const next = [...(list || [])]; const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next.map((s, k) => ({ ...s, step: k + 1 }));
    }),
    defaults: () => setValue("outreach_sequence", () => defaultSequence.map((s) => ({ ...s }))),
  };
  const kbOps = {
    add: () => setValue("kb", (list) => [...(list || []), { type: "link", name: "", value: "" }]),
    update: (i, patch) => setValue("kb", (list) => (list || []).map((e, k) => (k === i ? { ...e, ...patch } : e))),
    remove: (i) => setValue("kb", (list) => (list || []).filter((_, k) => k !== i)),
    file: (i, file) => {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === "string" ? reader.result : "";
        const words = (text.match(/\S+/g) || []).length;
        kbOps.update(i, { name: file.name, value: `${file.name} · ${Math.max(1, Math.round(file.size / 1024))} KB · ${words} words read client-side — full extraction coming soon` });
      };
      reader.onerror = () => kbOps.update(i, { name: file.name, value: `${file.name} · ${Math.max(1, Math.round(file.size / 1024))} KB — could not read the file` });
      reader.readAsText(file);
    },
  };
  const ctOps = {
    add: () => setValue("custom_tools", (list) => [...(list || []), { name: "", url: "", description: "" }]),
    update: (i, patch) => setValue("custom_tools", (list) => (list || []).map((t, k) => (k === i ? { ...t, ...patch } : t))),
    remove: (i) => setValue("custom_tools", (list) => (list || []).filter((_, k) => k !== i)),
  };

  const needsSaveCard = (what) => (
    <div className="glass p-10 text-center rise" data-testid="aux-needs-save">
      <h3 className="font-display text-lg font-semibold text-slate-100">{what} needs a saved agent</h3>
      <p className="text-sm text-slate-400 mt-1.5">Save this draft first — {what.toLowerCase()}, runs and history are tracked per saved agent.</p>
      <button onClick={save} disabled={saving} className="btn-primary mt-5" data-testid="aux-save-btn"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save draft"}</button>
    </div>
  );

  return (
    <div>
      <PageHeader overline={`${agent.audience} agent · ${agent.state || "—"}`} title={<span className="inline-flex items-center gap-3 flex-wrap">{agent.name || "Untitled agent"}<StatusPill status={agent.active === false ? "inactive" : (agent.status || "draft")} />{agent.version ? <span className="text-xs font-mono font-normal text-slate-500">v{agent.version}</span> : null}</span>}
        desc="Configure, test and publish. The chat test streams from the real model; the voice test runs a live ElevenLabs call in your browser." testId="builder-header"
        action={<div className="flex flex-wrap gap-2">
          <button onClick={save} className="btn-ghost" disabled={saving || publishing} data-testid="agent-save-btn"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save draft"}</button>
          <button onClick={openPublish} className="btn-primary" disabled={saving || publishing || diffBusy} data-testid="agent-publish-btn"><Rocket className="w-4 h-4" /> {publishing ? "Publishing…" : "Publish"}</button>
          <button onClick={publishVoice} className="btn-ghost" disabled={voicePublishing || saving || publishing} data-testid="agent-voice-publish-btn" title={agent.elevenlabs_agent_id ? "This agent has a live voice on ElevenLabs" : "Create this agent on ElevenLabs for real calls"}><Mic className="w-4 h-4" /> {voicePublishing ? "Publishing voice…" : agent.elevenlabs_agent_id ? "Voice live" : "Publish voice"}</button>
          {id && <button onClick={duplicate} className="btn-ghost" disabled={duplicating} data-testid="agent-duplicate-btn"><Copy className="w-4 h-4" /> {duplicating ? "Duplicating…" : "Duplicate"}</button>}
        </div>} />

      <div className="grid lg:grid-cols-12 gap-5">
        <nav className="lg:col-span-3">
          <div className="glass p-2 lg:sticky lg:top-6 flex lg:flex-col gap-1 overflow-x-auto" data-testid="builder-tabs">
            {TABS.map((t, i) => (
              <Fragment key={t.id}>
                {i > 0 && t.aux && !TABS[i - 1].aux && <div className="hidden lg:block my-2 border-t border-white/5 shrink-0" />}
                <button onClick={() => setTab(t.id)} className={`nav-item whitespace-nowrap ${tab === t.id ? "active" : ""}`} data-testid={`builder-tab-${t.id}`}>
                  <t.icon className="w-4 h-4 shrink-0" /><span>{t.label}</span>
                  {t.aux && !id && <span className="ml-auto hidden lg:inline text-[10px] font-mono text-slate-600">save first</span>}
                </button>
              </Fragment>
            ))}
          </div>
        </nav>

        <div className="lg:col-span-9 space-y-5">
          {tab === "basics" && (
            <>
              <Card title="Identity" desc="Who the agent talks to, in which region and language." testId="tab-basics">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Agent name"><input className="field" value={agent.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="agent-name-input" /></Field>
                  <Field label="Audience">
                    <div className="flex gap-1 p-1 glass-soft rounded-full w-max" data-testid="agent-audience-toggle">
                      {["farmer", "retailer"].map((a) => (
                        <button key={a} onClick={() => set("audience", a)} className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors ${agent.audience === a ? (a === "farmer" ? "bg-emerald-500 text-[#06110A] font-semibold" : "bg-amber-500 text-[#1A1000] font-semibold") : "text-slate-400"}`} data-testid={`agent-audience-${a}`}>{a}</button>
                      ))}
                    </div>
                  </Field>
                  <Field label="State"><Select value={agent.state} onChange={(v) => set("state", v)} options={(opts?.states || []).map((s) => s.name)} testId="agent-state-select" /></Field>
                  <Field label="Language"><Select value={agent.language} onChange={(v) => { set("language", v); setValue("additional_languages", (list) => (list || []).filter((l) => l !== v)); }} options={opts?.languages || []} testId="agent-language-select" /></Field>
                  <Field label="Additional languages" hint="The agent can switch to these if the lead speaks them.">
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {(opts?.languages || []).filter((l) => l !== agent.language).map((l) => {
                        const on = (agent.additional_languages || []).includes(l);
                        return <button key={l} type="button" onClick={() => toggle("additional_languages", l)} className={on ? "pill" : "pill-muted"} data-testid={`additional-language-${l}`}>{l}{on ? " ×" : ""}</button>;
                      })}
                      {!(opts?.languages || []).length && <span className="text-[11px] text-slate-500">Loading languages…</span>}
                    </div>
                  </Field>
                  <Field label="Dialect"><input className="field" value={agent.dialect || ""} onChange={(e) => set("dialect", e.target.value)} data-testid="agent-dialect-input" /></Field>
                  <Field label="Model" hint="LLMs offered by your ElevenLabs workspace; the agent's model is applied on publish."><Select value={agent.model} onChange={(v) => set("model", v)} options={modelOptions} testId="agent-model-select" /></Field>
                  <Field label="Season crops (context)"><input className="field" value={agent.crops || ""} onChange={(e) => set("crops", e.target.value)} data-testid="agent-crops-input" /></Field>
                  <Field label="Caller ID (optional)" hint={integration?.configured ? (phoneOptions.length ? "Outbound number for campaigns. Defaults to the account number." : "No phone numbers imported in ElevenLabs yet.") : "ElevenLabs not configured — numbers appear once the API key is set."}>
                    <Select value={agent.phone_number_id || ""} onChange={(v) => set("phone_number_id", v)} options={phoneOptions} placeholder="Default account number" testId="agent-phone-select" />
                  </Field>
                  <Field label="Active">
                    <div className="flex items-center gap-3 pt-1.5">
                      <Toggle on={agent.active !== false} onChange={(v) => set("active", v)} testId="agent-active-toggle" />
                      <span className="text-xs text-slate-400">{agent.active !== false ? "Active — campaigns can use this agent" : "Inactive — paused for campaigns"}</span>
                    </div>
                  </Field>
                </div>
              </Card>

              <Card title="Voice" desc="Four supported voices; press play to hear a sample, click the card to select. Republish the voice after changing language or voice." testId="tab-voice">
                <div className="grid sm:grid-cols-2 gap-2">
                  {voices.map((v) => {
                    const selected = agent.voice === v.id;
                    const playing = playingVoice === v.id;
                    const live = liveVoiceFor(v);
                    const name = live?.name ? live.name.split(/\s+[-–]\s+/)[0] : (v.name || v.id);
                    const gender = live?.labels?.gender || v.gender;
                    const unavailable = Boolean(v.unavailable);
                    return (
                      <div key={v.id} className={`rounded-lg border flex items-center gap-2 p-2.5 transition-colors ${selected ? "border-emerald-500/60 bg-emerald-500/10" : "border-white/10 hover:border-white/20"} ${unavailable ? "opacity-60" : ""}`} data-testid={`voice-${v.id}`}>
                        <button type="button" disabled={unavailable} onClick={() => toggleVoicePreview(v.id)} className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center border transition-colors disabled:cursor-not-allowed ${playing ? "bg-emerald-500 border-emerald-500 text-[#06110A]" : "border-white/15 text-emerald-300 hover:bg-white/[0.06]"}`} title={unavailable ? "This voice is not available in the ElevenLabs account" : playing ? "Stop the preview" : "Play a voice sample"} data-testid={`voice-preview-${v.id}`} aria-label={playing ? `Stop ${name} preview` : `Play ${name} preview`}>
                          {playing ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                        </button>
                        <button type="button" disabled={unavailable} onClick={() => set("voice", v.id)} className="flex-1 min-w-0 text-left disabled:cursor-not-allowed" data-testid={`voice-select-${v.id}`}>
                          <p className="text-sm text-slate-100 flex items-center gap-1.5">{name} <span className="text-slate-500 text-xs">· {gender}</span>{selected && <span className="text-[10px] font-mono uppercase text-emerald-300 ml-auto">selected</span>}</p>
                          <p className="text-[11px] text-slate-500 truncate">{unavailable ? "Unavailable — choose one of the supported voices" : (v.tone || "ElevenLabs voice")}</p>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </>
          )}

          {tab === "prompts" && (
            <>
              <Card title="System prompt" desc="The agent's core instructions; placeholders are filled from this config." testId="tab-system-prompt">
                <textarea className="field min-h-[220px] font-mono text-xs leading-relaxed" value={agent.prompt || ""} onChange={(e) => set("prompt", e.target.value)} data-testid="agent-prompt-textarea" />
                <p className="text-[11px] text-slate-500">Placeholders: {"{agent_name} {language} {dialect} {company} {crop_hint}"}</p>
              </Card>
              <Card title="Voice call opener" desc="First thing said when a call is answered." testId="tab-first-message">
                <input className="field" value={agent.first_message || ""} onChange={(e) => set("first_message", e.target.value)} placeholder={defaultFirstMessage(agent.audience)} data-testid="agent-first-message-input" />
                <p className="text-[11px] text-slate-500">Placeholders: {"{name} {agent} {company}"}. {agent.first_message ? "" : "Empty — the greeting falls back to the system prompt. "}
                  <button type="button" className="underline hover:text-slate-300" onClick={() => set("first_message", defaultFirstMessage(agent.audience))} data-testid="agent-first-message-suggest-btn">Insert suggested opener</button>
                </p>
              </Card>
              <Card title="Success goal" desc="Natural-language definition of a successful conversation — this is what post-call analysis will judge." testId="tab-goal">
                <textarea className="field min-h-[110px]" value={agent.goal || ""} onChange={(e) => set("goal", e.target.value)} placeholder="e.g. Get the farmer to agree to a corn seed order request for the upcoming season (quantity + delivery preference)." data-testid="agent-goal-textarea" />
              </Card>
              <Card title="Channel prompts" desc="Per-channel guidance layered on top of the system prompt." testId="tab-channel-prompts">
                <div className="space-y-4">
                  {(agent.channels || []).map((ch) => (
                    <Field key={ch} label={channelName(ch)} hint={ch === "voice_call" ? "Voice calls: this is applied on top of the system prompt — keep sentences short and spoken." : undefined}>
                      <textarea className="field min-h-[90px] text-sm" value={(agent.channel_prompts || {})[ch] || ""} onChange={(e) => set("channel_prompts", { ...(agent.channel_prompts || {}), [ch]: e.target.value })} data-testid={`channel-prompt-${ch}`}
                        placeholder={ch === "whatsapp" ? "WhatsApp tone — concise, respectful, no jargon." : "Voice call specifics — confirm you are speaking to {name} before the pitch."} />
                    </Field>
                  ))}
                  {!(agent.channels || []).length && <p className="text-xs text-amber-300">No channels selected — pick at least one in “Channels & templates” first.</p>}
                </div>
              </Card>
            </>
          )}

          {tab === "channels" && (
            <>
              <Card title="Channels" desc="Where this agent reaches leads. Publishing needs at least one." testId="tab-channels">
                <div className="grid sm:grid-cols-2 gap-2">
                  {channels.map((c) => {
                    const on = (agent.channels || []).includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggle("channels", c.id)} className={`text-left p-3 rounded-xl border transition-colors ${on ? "border-emerald-500/60 bg-emerald-500/10" : "border-white/10 hover:border-white/20"}`} data-testid={`channel-${c.id}`}>
                        <p className="text-sm text-slate-100 flex items-center gap-2">{channelIcon(c.id)} {c.name} {on && <span className="ml-auto text-[11px] font-mono text-emerald-300">on</span>}</p>
                        <p className="text-[11px] text-slate-500 mt-1">{c.desc}</p>
                      </button>
                    );
                  })}
                </div>
                {!(agent.channels || []).length && <p className="text-xs text-amber-300">Select at least one channel — publishing will fail without it.</p>}
              </Card>
              <Card title="WhatsApp template" desc="First business message uses this template; after the lead replies the agent chats free-form." testId="tab-whatsapp-template">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Template name"><input className="field" value={wa.name || ""} onChange={(e) => set("whatsapp_template", { ...wa, name: e.target.value })} placeholder="agrivoice_kisan_followup" data-testid="wa-template-name" /></Field>
                  <Field label="Language code"><input className="field" value={wa.language || ""} onChange={(e) => set("whatsapp_template", { ...wa, language: e.target.value })} placeholder="hi" data-testid="wa-template-language" /></Field>
                </div>
                <Field label="Body">
                  <textarea className="field min-h-[110px]" value={wa.body || ""} onChange={(e) => set("whatsapp_template", { ...wa, body: e.target.value })} data-testid="wa-template-body" placeholder="Namaste {name}, {company} here. Aapki {crop} fasal ke liye zaroori salah — 2 min baat kar sakte hain?" />
                  <p className="text-[11px] text-slate-500 mt-1">Placeholders: {"{name} {company} {crop} {retailer}"} · {(wa.body || "").length} characters</p>
                </Field>
              </Card>
            </>
          )}

          {tab === "guardrails" && (
            <Card title="Guardrails" desc="Hard limits the orchestrator enforces across WhatsApp and calls." testId="tab-guardrails"
              action={<button onClick={() => set("guardrails", { ...(opts?.guardrail_defaults || FALLBACK_GUARDRAILS) })} className="btn-ghost btn-sm shrink-0" data-testid="guardrails-reset-btn"><RefreshCw className="w-3.5 h-3.5" /> Reset to defaults</button>}>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Daily messages per lead"><input type="number" min="0" className="field" value={gr.daily_messages_per_lead ?? ""} onChange={(e) => setGuard("daily_messages_per_lead", numOrEmpty(e.target.value))} data-testid="guard-daily-messages" /></Field>
                <Field label="Max messages per lead"><input type="number" min="0" className="field" value={gr.max_messages_per_lead ?? ""} onChange={(e) => setGuard("max_messages_per_lead", numOrEmpty(e.target.value))} data-testid="guard-max-messages" /></Field>
                <Field label="Max call attempts"><input type="number" min="0" className="field" value={gr.max_call_attempts ?? ""} onChange={(e) => setGuard("max_call_attempts", numOrEmpty(e.target.value))} data-testid="guard-max-calls" /></Field>
                <Field label="Daily calls (all leads)"><input type="number" min="0" className="field" value={gr.daily_calls_total ?? ""} onChange={(e) => setGuard("daily_calls_total", numOrEmpty(e.target.value))} data-testid="guard-daily-calls" /></Field>
                <Field label="Call window start"><input type="time" className="field" value={gr.call_window_start || ""} onChange={(e) => setGuard("call_window_start", e.target.value)} data-testid="guard-window-start" /></Field>
                <Field label="Call window end"><input type="time" className="field" value={gr.call_window_end || ""} onChange={(e) => setGuard("call_window_end", e.target.value)} data-testid="guard-window-end" /></Field>
                <Field label="Timezone"><Select value={gr.timezone || "Asia/Kolkata"} onChange={(v) => setGuard("timezone", v)} options={TIMEZONES} testId="guard-timezone" /></Field>
                <Field label="Min minutes between calls"><input type="number" min="0" className="field" value={gr.min_minutes_between_calls ?? ""} onChange={(e) => setGuard("min_minutes_between_calls", numOrEmpty(e.target.value))} data-testid="guard-between-calls" /></Field>
                <Field label="Min minutes between messages"><input type="number" min="0" className="field" value={gr.min_minutes_between_messages ?? ""} onChange={(e) => setGuard("min_minutes_between_messages", numOrEmpty(e.target.value))} data-testid="guard-between-messages" /></Field>
                <Field label="Stop on reply">
                  <div className="flex items-center gap-3 pt-1.5"><Toggle on={gr.stop_on_reply !== false} onChange={(v) => setGuard("stop_on_reply", v)} testId="guard-stop-on-reply" /><span className="text-xs text-slate-400">{gr.stop_on_reply !== false ? "Stop follow-ups as soon as the lead replies" : "Keep running the sequence after a reply"}</span></div>
                </Field>
              </div>
            </Card>
          )}

          {tab === "sequence" && (
            <Card title="Outreach sequence" desc="Ordered steps per lead. Waits are counted from the previous step." testId="tab-sequence"
              action={<button onClick={seqOps.defaults} className="btn-ghost btn-sm shrink-0" data-testid="sequence-defaults-btn"><ListChecks className="w-3.5 h-3.5" /> Load default sequence</button>}>
              <div className="glass-soft p-3 flex flex-wrap items-center gap-2" data-testid="sequence-preview">
                {seq.map((s, i) => (
                  <Fragment key={i}>
                    {i > 0 && <span className="text-[11px] font-mono text-amber-300/80">wait {fmtDelay(s.delay_minutes)} →</span>}
                    <span className="pill-muted flex items-center gap-1.5">{channelIcon(s.channel, "w-3 h-3")} {channelName(s.channel)}</span>
                  </Fragment>
                ))}
                {!seq.length && <span className="text-xs text-slate-500">No steps yet — add one below or load the default sequence.</span>}
              </div>
              <div className="space-y-2">
                {seq.map((s, i) => (
                  <div key={i} className="glass-soft p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`sequence-step-${i}`}>
                    <span className="font-mono text-[11px] text-emerald-300 w-6 shrink-0">#{i + 1}</span>
                    <Select className="sm:w-40" value={s.channel} onChange={(v) => seqOps.update(i, { channel: v })} options={channelOptions} testId={`sequence-channel-${i}`} />
                    <div className="flex items-center gap-1.5"><input type="number" min="0" className="field w-24" value={s.delay_minutes ?? 0} onChange={(e) => seqOps.update(i, { delay_minutes: numOrEmpty(e.target.value) })} data-testid={`sequence-delay-${i}`} /><span className="text-[11px] text-slate-500 font-mono">min wait</span></div>
                    <input className="field flex-1 min-w-[8rem]" value={s.label || ""} onChange={(e) => seqOps.update(i, { label: e.target.value })} placeholder="Step label" data-testid={`sequence-label-${i}`} />
                    <div className="flex items-center gap-0.5 shrink-0 self-end sm:self-center">
                      <button onClick={() => seqOps.move(i, -1)} disabled={i === 0} className="text-slate-500 hover:text-slate-200 p-1.5 disabled:opacity-30" data-testid={`sequence-up-${i}`} title="Move up"><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => seqOps.move(i, 1)} disabled={i === seq.length - 1} className="text-slate-500 hover:text-slate-200 p-1.5 disabled:opacity-30" data-testid={`sequence-down-${i}`} title="Move down"><ChevronDown className="w-4 h-4" /></button>
                      <button onClick={() => seqOps.remove(i)} className="text-slate-500 hover:text-rose-300 p-1.5" data-testid={`sequence-remove-${i}`} title="Remove step"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={seqOps.add} className="btn-ghost btn-sm" data-testid="sequence-add-btn"><Plus className="w-3.5 h-3.5" /> Add step</button>
            </Card>
          )}

          {tab === "knowledge" && (
            <>
              <Card title="Knowledge base" desc="Live data sources the agent can read. On publish these are synced to ElevenLabs as knowledge-base documents." testId="tab-kb-sources">
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {(opts?.kb_sources || []).map((k) => (
                    <label key={k.id} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                      <input type="checkbox" checked={(agent.kb_sources || []).includes(k.id)} onChange={() => toggle("kb_sources", k.id)} className="accent-emerald-500" data-testid={`kb-${k.id}`} />{k.name}
                    </label>
                  ))}
                </div>
              </Card>
              <Card title="Custom knowledge" desc="Links, documents and notes layered over the sources above — links and text notes become ElevenLabs documents on publish." testId="tab-kb-entries"
                action={<button onClick={kbOps.add} className="btn-ghost btn-sm shrink-0" data-testid="kb-add-btn"><Plus className="w-3.5 h-3.5" /> Add entry</button>}>
                {kb.length === 0 && <p className="text-xs text-slate-500">No custom entries yet. Add a product guide link, a PDF or a text note.</p>}
                {kb.map((e, i) => (
                  <div key={i} className="glass-soft p-3 space-y-2" data-testid={`kb-entry-${i}`}>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Select className="sm:w-36" value={e.type || "link"} onChange={(v) => kbOps.update(i, { type: v })} options={[{ value: "link", label: "Link" }, { value: "document", label: "Document" }, { value: "text", label: "Text note" }]} testId={`kb-type-${i}`} />
                      <input className="field flex-1" value={e.name || ""} onChange={(ev) => kbOps.update(i, { name: ev.target.value })} placeholder="Name — e.g. Maize nutrition guide" data-testid={`kb-name-${i}`} />
                      <button onClick={() => kbOps.remove(i)} className="text-slate-500 hover:text-rose-300 p-2 self-center" data-testid={`kb-remove-${i}`} title="Remove entry"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {e.type === "document" ? (
                      <div>
                        <input type="file" accept=".pdf,.txt,.md,.csv,.docx" className="field" onChange={(ev) => kbOps.file(i, ev.target.files?.[0])} data-testid={`kb-file-${i}`} />
                        <p className="text-[11px] text-slate-500 mt-1">{e.value || "The file is read client-side — a short note and the filename are stored. Full ingestion runs server-side later."}</p>
                      </div>
                    ) : (
                      <input className="field" value={e.value || ""} onChange={(ev) => kbOps.update(i, { value: ev.target.value })} placeholder={e.type === "link" ? "https://…" : "What should the agent know?"} data-testid={`kb-value-${i}`} />
                    )}
                  </div>
                ))}
              </Card>
              <Card title="Workflow tools" desc="The agent calls these workflows as tools. Order booking shows up on the conversation after the call." testId="tab-workflows">
                <Select value="" onChange={(wid) => { if (!wid) return; setValue("workflow_ids", (list) => ((list || []).includes(wid) ? list : [...(list || []), wid])); }} options={workflows.filter((w) => !(agent.workflow_ids || []).includes(w.id)).map((w) => ({ value: w.id, label: w.name }))} placeholder="Add a workflow, e.g. Order booking" testId="agent-workflow-select" />
                <div className="flex flex-wrap gap-2">{(agent.workflow_ids || []).map((wid) => { const w = workflows.find((row) => row.id === wid); return <button type="button" key={wid} className="pill" data-testid={`agent-workflow-${w?.slug || wid}`} onClick={() => setValue("workflow_ids", (list) => (list || []).filter((item) => item !== wid))}>{w?.name || wid} ×</button>; })}{(agent.workflow_ids || []).length === 0 && <span className="text-[11px] text-slate-500">No workflow tools attached.</span>}</div>
              </Card>
              <Card title="Custom tools" desc="HTTP endpoints the agent can call mid-conversation." testId="tab-custom-tools"
                action={<button onClick={ctOps.add} className="btn-ghost btn-sm shrink-0" data-testid="custom-tool-add-btn"><Plus className="w-3.5 h-3.5" /> Add tool</button>}>
                {ct.length === 0 && <p className="text-xs text-slate-500">No custom tools yet.</p>}
                {ct.map((t, i) => (
                  <div key={i} className="glass-soft p-3 space-y-2" data-testid={`custom-tool-${i}`}>
                    <div className="flex gap-2">
                      <input className="field sm:w-48" value={t.name || ""} onChange={(e) => ctOps.update(i, { name: e.target.value })} placeholder="Tool name" data-testid={`custom-tool-name-${i}`} />
                      <input className="field flex-1" value={t.url || ""} onChange={(e) => ctOps.update(i, { url: e.target.value })} placeholder="https://api.example.com/inventory" data-testid={`custom-tool-url-${i}`} />
                      <button onClick={() => ctOps.remove(i)} className="text-slate-500 hover:text-rose-300 p-2 self-center" data-testid={`custom-tool-remove-${i}`} title="Remove tool"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <input className="field" value={t.description || ""} onChange={(e) => ctOps.update(i, { description: e.target.value })} placeholder="What does it do, and when should the agent call it?" data-testid={`custom-tool-desc-${i}`} />
                  </div>
                ))}
              </Card>
            </>
          )}

          {tab === "test" && (
            <div className="space-y-4" data-testid="tab-test">
              <div className="flex gap-1 p-1 glass-soft rounded-full w-max" data-testid="test-subtabs">
                {[["chat", "Chat"], ["voice", "Voice"]].map(([k, l]) => (
                  <button key={k} onClick={() => setTestTab(k)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${testTab === k ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" : "text-slate-400"}`} data-testid={`test-subtab-${k}`}>{l}</button>
                ))}
              </div>
              {testTab === "chat" ? <TestConsole agent={agent} agentId={id} /> : <VoiceTestPanel agent={agent} agentId={id} dirty={dirty} onNeedSave={save} />}
            </div>
          )}

          {tab === "campaigns" && (id ? <CampaignsTab agentId={id} /> : needsSaveCard("Campaigns"))}
          {tab === "runs" && (id ? <RunsTab agentId={id} /> : needsSaveCard("Runs"))}
          {tab === "history" && (id ? <HistoryTab agentId={id} onLoadSnapshot={loadSnapshot} onAfterPublish={reloadAgent} currentVersion={agent.version} /> : needsSaveCard("History"))}
        </div>
      </div>

      <Modal open={Boolean(publishPreview)} onClose={() => setPublishPreview(null)} title={`Publish “${agent.name || "agent"}”`} wide testId="publish-preview-modal">
        {publishPreview && (
          <div className="space-y-4">
            {publishPreview.first_publish ? (
              <p className="text-sm text-slate-400">First publish — this stores the initial version snapshot and provisions the ElevenLabs voice agent (prompt, model, voice, knowledge base).</p>
            ) : (
              <>
                <p className="text-sm text-slate-400">Publishing now applies <span className="text-slate-200">{publishPreview.changes.length ? `${publishPreview.changes.length} change${publishPreview.changes.length === 1 ? "" : "s"}` : "no changes"}</span> since the last published v{publishPreview.base_version}, then refreshes the ElevenLabs agent.</p>
                <DiffList changes={publishPreview.changes} empty={`No changes since v${publishPreview.base_version} — publishing again just creates a new snapshot and refreshes the voice agent.`} />
              </>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setPublishPreview(null)} className="btn-ghost flex-1" data-testid="publish-cancel-btn">Cancel</button>
              <button onClick={doPublish} disabled={publishing} className="btn-primary flex-1" data-testid="publish-confirm-btn"><Rocket className="w-4 h-4" /> {publishing ? "Publishing…" : `Publish v${(agent.version || 1) + (agent.status === "published" ? 1 : 0)}`}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ---------- aux tabs ----------

function CampaignsTab({ agentId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    api.get(`/agents/${agentId}/campaigns`).then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setBusy(false));
  };
  useEffect(() => { setRows(null); load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="glass rise" data-testid="tab-campaigns">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div><h3 className="font-display font-semibold text-slate-100">Campaigns using this agent</h3><p className="text-[11px] text-slate-500 mt-0.5">Leads, conversions and the campaign goal.</p></div>
        <button onClick={load} disabled={busy} className="btn-ghost btn-sm" data-testid="campaigns-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr>{["Campaign", "Status", "Leads", "Conversions", "Goal"].map((h) => <th key={h} className="table-head">{h}</th>)}</tr></thead>
          <tbody>
            {rows?.map((c) => (
              <tr key={c.id} className="hover:bg-white/[0.02]" data-testid={`campaign-row-${c.id}`}>
                <td className="table-cell font-medium text-slate-100">{c.name}</td>
                <td className="table-cell"><StatusPill status={c.status} /></td>
                <td className="table-cell font-mono">{c.processed ?? 0}/{c.total ?? 0}</td>
                <td className="table-cell font-mono">{c.conversions ?? c.orders ?? 0}</td>
                <td className="table-cell text-xs text-slate-500 max-w-[16rem] truncate" title={c.goal || ""}>{c.goal || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows && rows.length === 0 && <p className="text-sm text-slate-500 px-4 py-8">No campaigns use this agent yet. Create one from the Campaigns page.</p>}
        {!rows && <p className="text-sm text-slate-500 px-4 py-8">Loading campaigns…</p>}
      </div>
    </div>
  );
}

function RunsTab({ agentId }) {
  const [rows, setRows] = useState(null);
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    api.get(`/agents/${agentId}/runs`, { params: { limit: 50 } }).then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setBusy(false));
  };
  useEffect(() => { setRows(null); load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!auto) return undefined; const t = setInterval(load, 10000); return () => clearInterval(t); }, [auto, agentId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="glass rise" data-testid="tab-runs">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 gap-3 flex-wrap">
        <div><h3 className="font-display font-semibold text-slate-100">Agent runs</h3><p className="text-[11px] text-slate-500 mt-0.5">Every WhatsApp and call step executed for this agent.</p></div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2"><Toggle on={auto} onChange={setAuto} testId="runs-auto-toggle" /><span className="text-[11px] font-mono text-slate-500">auto 10s</span></div>
          <button onClick={load} disabled={busy} className="btn-ghost btn-sm" data-testid="runs-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr>{["Time", "Lead", "Channel", "Step", "Status", "Reason", "Duration"].map((h) => <th key={h} className="table-head">{h}</th>)}</tr></thead>
          <tbody>
            {rows?.map((r) => (
              <tr key={r.id} className="hover:bg-white/[0.02]" data-testid={`run-row-${r.id}`}>
                <td className="table-cell whitespace-nowrap text-xs text-slate-500">{timeAgo(r.executed_at || r.created_at)}</td>
                <td className="table-cell">{r.lead_name || r.lead_id || "—"}</td>
                <td className="table-cell"><span className="pill-muted flex w-max items-center gap-1.5">{r.channel === "voice_call" ? <Phone className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}{r.channel || "—"}</span></td>
                <td className="table-cell font-mono text-xs">#{r.step ?? "—"}</td>
                <td className="table-cell"><StatusPill status={r.status} /></td>
                <td className="table-cell text-xs text-slate-500 max-w-[14rem] truncate" title={r.reason || ""}>{r.reason || "—"}</td>
                <td className="table-cell font-mono text-xs">{r.duration_sec != null ? `${r.duration_sec}s` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows && rows.length === 0 && <p className="text-sm text-slate-500 px-4 py-8">No runs yet — launch a campaign with this agent and steps will appear here live.</p>}
        {!rows && <p className="text-sm text-slate-500 px-4 py-8">Loading runs…</p>}
      </div>
    </div>
  );
}

function HistoryTab({ agentId, onLoadSnapshot, onAfterPublish, currentVersion }) {
  const [versions, setVersions] = useState(null);
  const [audit, setAudit] = useState(null);
  const [diff, setDiff] = useState(null); // {version, changes, current_version, compared_to_published}
  const [publishTarget, setPublishTarget] = useState(null); // {version, changes}
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    api.get(`/agents/${agentId}/versions`).then((r) => setVersions(r.data || [])).catch(() => setVersions([]));
    api.get(`/agents/${agentId}/audit`).then((r) => setAudit(r.data || [])).catch(() => setAudit([]));
  };
  useEffect(() => { setVersions(null); setAudit(null); loadAll(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDiff = async (version) => {
    const { data } = await api.get(`/agents/${agentId}/versions/${version}/diff`);
    return data || {};
  };

  const openDiff = async (version) => {
    setBusy(true);
    try {
      const data = await fetchDiff(version);
      setDiff({ ...data, version });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the version diff");
    } finally { setBusy(false); }
  };

  const openPublish = async (version) => {
    setBusy(true);
    try {
      const data = await fetchDiff(version);
      setPublishTarget({ version, changes: data.changes || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the version diff");
    } finally { setBusy(false); }
  };

  const publishVersion = async () => {
    if (!publishTarget?.version) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/agents/${agentId}/versions/${publishTarget.version}/publish`, {}, { timeout: 90000 });
      const kb = data.elevenlabs?.kb_docs ? ` · ${data.elevenlabs.kb_docs} KB docs` : "";
      toast.success(`v${publishTarget.version} republished as v${data.version}${data.elevenlabs?.agent_id ? " · voice synced" : ""}${kb}`);
      if (data.elevenlabs?.error) toast.warning(`Voice sync skipped: ${data.elevenlabs.error}`);
      setPublishTarget(null);
      await onAfterPublish?.();
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publishing the version failed");
    } finally { setBusy(false); }
  };

  return (
    <>
    <div className="space-y-5" data-testid="tab-history">
      <div className="glass rise">
        <div className="px-4 py-3 border-b border-white/5">
          <h3 className="font-display font-semibold text-slate-100 flex items-center gap-2"><History className="w-4 h-4 text-emerald-300" /> Versions</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Every publish stores a snapshot. Compare one against the current config, republish it, or load it into the editor — loading changes nothing until you save.</p>
        </div>
        <div className="divide-y divide-white/5">
          {versions?.map((v, i) => (
            <div key={v.id || `${v.version}-${i}`} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`version-row-${v.version}`}>
              <span className="pill">v{v.version}</span>
              {v.version === currentVersion && <span className="pill-cyan">current</span>}
              <span className="text-xs text-slate-500 flex-1 min-w-[8rem]">{timeAgo(v.created_at)} · {v.created_by || "system"}</span>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <button onClick={() => openDiff(v.version)} disabled={!v.snapshot || busy} className="btn-ghost btn-sm" data-testid={`version-diff-${v.version}`} title="Compare with the current config"><GitCompare className="w-3.5 h-3.5" /> Diff</button>
                <button onClick={() => openPublish(v.version)} disabled={!v.snapshot || busy} className="btn-ghost btn-sm" data-testid={`version-publish-${v.version}`} title="Make this snapshot the current config and publish it as a new version"><Rocket className="w-3.5 h-3.5" /> Publish</button>
                <button onClick={() => onLoadSnapshot(v.snapshot, v.version)} disabled={!v.snapshot} className="btn-ghost btn-sm" data-testid={`version-load-${v.version}`} title={v.snapshot ? "Fill the editor with this snapshot" : "Snapshot not available"}><BookOpen className="w-3.5 h-3.5" /> Load into editor</button>
              </div>
            </div>
          ))}
          {versions && versions.length === 0 && <p className="text-sm text-slate-500 px-4 py-6">No versions yet — publish the agent to create the first snapshot.</p>}
          {!versions && <p className="text-sm text-slate-500 px-4 py-6">Loading versions…</p>}
        </div>
      </div>
      <div className="glass rise">
        <div className="px-4 py-3 border-b border-white/5">
          <h3 className="font-display font-semibold text-slate-100 flex items-center gap-2"><Activity className="w-4 h-4 text-amber-300" /> Audit log</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Everything that happened to this agent, newest first.</p>
        </div>
        <div className="divide-y divide-white/5">
          {audit?.map((a, i) => (
            <div key={i} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3" data-testid={`audit-row-${i}`}>
              <span className="font-mono text-[11px] text-emerald-300 sm:w-44 shrink-0">{a.action}</span>
              <span className="text-xs text-slate-400 flex-1 break-words">{metaSummary(a.metadata) || "—"}</span>
              <span className="text-[11px] text-slate-500 font-mono shrink-0">{a.actor_id || "system"} · {timeAgo(a.created_at)}</span>
            </div>
          ))}
          {audit && audit.length === 0 && <p className="text-sm text-slate-500 px-4 py-6">No audit entries yet.</p>}
          {!audit && <p className="text-sm text-slate-500 px-4 py-6">Loading audit log…</p>}
        </div>
      </div>
    </div>

    <Modal open={Boolean(diff)} onClose={() => setDiff(null)} title={diff ? `v${diff.version} vs current config` : "Version diff"} wide testId="version-diff-modal">
      {diff && (
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Changes from v{diff.version} to the current config (the editor draft{currentVersion != null ? ` · agent at v${currentVersion}` : ""}).</p>
          <DiffList changes={diff.changes} empty={`v${diff.version} is identical to the current config.`} />
          <button onClick={() => setDiff(null)} className="btn-ghost w-full" data-testid="version-diff-close">Close</button>
        </div>
      )}
    </Modal>

    <Modal open={Boolean(publishTarget)} onClose={() => setPublishTarget(null)} title={publishTarget ? `Publish v${publishTarget.version}` : "Publish version"} wide testId="version-publish-modal">
      {publishTarget && (
        <div className="space-y-4">
          <p className="text-sm text-slate-400">This replaces the current config with the v{publishTarget.version} snapshot and publishes it as a new version — the ElevenLabs agent (prompt, model, voice, knowledge base) is refreshed too.</p>
          <DiffList changes={publishTarget.changes} empty={`v${publishTarget.version} is identical to the current config — publishing just creates a new version snapshot.`} />
          <div className="flex gap-2">
            <button onClick={() => setPublishTarget(null)} className="btn-ghost flex-1">Cancel</button>
            <button onClick={publishVersion} disabled={busy} className="btn-primary flex-1" data-testid="version-publish-confirm"><Rocket className="w-4 h-4" /> {busy ? "Publishing…" : `Publish v${publishTarget.version}`}</button>
          </div>
        </div>
      )}
    </Modal>
    </>
  );
}

// ---------- chat test console (unchanged behaviour) ----------

function TestConsole({ agent, agentId }) {
  const [msgs, setMsgs] = useState([{ role: "assistant", content: `Namaste! I'm ${agent.name}. Say "hello" to start a test ${agent.audience === "farmer" ? "farm visit call" : "retailer stock review"}.` }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const next = [...msgs, { role: "user", content: input }];
    setMsgs([...next, { role: "assistant", content: "" }]);
    setInput(""); setBusy(true);
    try {
      const res = await fetch(`${API}/agents/${agentId || "draft"}/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ messages: next.filter((m) => m.content), agent }) });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "", acc = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) { const line = p.replace(/^data: /, ""); if (line === "[DONE]") continue; acc += line.replace(/\\n/g, "\n"); setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: acc }]); }
      }
    } catch { toast.error("Agent did not respond"); } finally { setBusy(false); }
  };

  return (
    <div className="glass rise flex flex-col h-[640px]" data-testid="agent-test-console">
      <div className="p-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3"><div className="relative w-9 h-9"><span className={`absolute inset-0 rounded-full bg-emerald-500/40 ${busy ? "pulse-ring" : ""}`} /><div className="absolute inset-0 rounded-full bg-emerald-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#06110A]" /></div></div><div><p className="text-sm font-semibold text-slate-100">Test console</p><p className="text-[11px] font-mono text-slate-500">{agent.language} · {agent.dialect} · {agent.model}</p></div></div>
        {busy && <Waveform bars={7} />}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.map((m, i) => <div key={i} className={m.role === "assistant" ? "wa-bubble-agent whitespace-pre-wrap" : "wa-bubble-contact"} data-testid={`console-msg-${i}`}>{m.content || <span className="text-slate-500">…</span>}</div>)}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-white/5 flex gap-2">
        <input className="field" placeholder="Type as the farmer / retailer…" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="console-input" />
        <button onClick={send} disabled={busy} className="btn-primary px-4" data-testid="console-send-btn"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
